import stockData from '@/data/ground-textures.json';

/**
 * The shipped library of ground materials — what a site can be painted with before anybody
 * describes, forges or uploads anything.
 *
 * ## Why this is a catalog and not more built-ins
 *
 * The obvious way to ship more materials is to add them to `BUILT_IN_PAINTED_LAYERS`. That is wrong,
 * and the reason is the splat: a palette entry costs one of `MAX_PAINTED_LAYERS` channels, and the
 * ground shader samples a whole array-texture page per four of them whether the garden uses them or
 * not. Sixteen shipped materials would spend the entire budget before the user painted a stroke, and
 * a courtyard in brick and concrete would have no room for the one material it actually needed.
 *
 * So the library is a *catalog you pick from*, exactly like the flora and prop catalogs: it holds as
 * many materials as are worth drawing, and taking one mints a palette layer at that moment. What a
 * site pays for is what a site uses. The same split is why the pipeline can be told "these thirty
 * exist" without any of them being installed.
 *
 * ## Why the metadata is here and the pixels are not
 *
 * A record is small — twelve fields, no image — so the whole catalog can be indexed at start-up and
 * searched without fetching anything. The swatch is a PNG in `src/assets/swatches`, emitted by the
 * bundler as a hashed URL and fetched only when a material is actually taken. See `StockLibrary.ts`.
 *
 * Generated by `scripts/swatches/build-swatches.mjs`; not hand-edited. `color` in particular is
 * *measured* off the pixels that shipped rather than typed, for the reason `docs/GROUND_MATERIALS.md`
 * gives: the chip, the brush ring and the plan overlay have to agree with what the ground shows.
 */

/**
 * What kind of surface this is — the sections a picker shows.
 *
 * Grouped by how a material behaves underfoot and in a bed rather than by what it is made of, since
 * that is the distinction a designer is actually making when they reach for one. Bluestone and brick
 * are both `paving` despite sharing no geology; decomposed granite and pea shingle are both
 * `aggregate` despite one being walked on and the other raked.
 */
export type StockTextureFamily = 'paving' | 'aggregate' | 'soft' | 'turf' | 'water';

export const STOCK_TEXTURE_FAMILIES: readonly StockTextureFamily[] = [
  'paving',
  'aggregate',
  'soft',
  'turf',
  'water',
];

export interface StockTextureDefinition {
  id: string;
  name: string;
  family: StockTextureFamily;
  /** Free-text search terms — trade names, uses, colours. What someone would type looking for it. */
  tags: string[];
  /** File name within `src/assets/swatches`. */
  file: string;
  /** The swatch's measured average colour, `#rrggbb`. Not the authored intent — the actual pixels. */
  color: string;
  roughness: number;
  plantable: boolean;
  /** Metres one tile of the swatch covers on the ground. */
  tileMeters: number;
  /** Typical size of one unit — a paver, a chip, a bark shred — in metres. */
  grainMeters: number;
  /** One line for the picker and for the layer once it is taken. */
  hint: string;
  /** What this material *is*, in the language a render prompt wants. Reaches the capture manifest. */
  character: string;
  /** How the swatch was arrived at, so the numbers above are auditable rather than asserted. */
  grounding: string;
}

interface StockCatalogFile {
  catalogVersion: string;
  schemaVersion: number;
  textures: unknown[];
}

export class StockTextureCatalog {
  private readonly byId = new Map<string, StockTextureDefinition>();
  readonly version: string;

  constructor(file: StockCatalogFile) {
    this.version = file.catalogVersion;
    for (const record of file.textures) {
      assertValidStockTexture(record);
      if (this.byId.has(record.id)) {
        throw new Error(`Duplicate ground texture id in catalog: ${record.id}`);
      }
      this.byId.set(record.id, record);
    }
  }

  get size(): number {
    return this.byId.size;
  }

  /** Every material, in catalog order. */
  list(): StockTextureDefinition[] {
    return [...this.byId.values()];
  }

  get(id: string): StockTextureDefinition | undefined {
    return this.byId.get(id);
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  byFamily(family: StockTextureFamily): StockTextureDefinition[] {
    return this.list().filter((texture) => texture.family === family);
  }

  /** Families actually present, in canonical order — what a picker should show as sections. */
  families(): StockTextureFamily[] {
    return STOCK_TEXTURE_FAMILIES.filter((family) => this.byFamily(family).length > 0);
  }

  /**
   * Materials matching a phrase, best first.
   *
   * Ranked rather than filtered, and over name, tags and hint together, because the word someone
   * reaches for is very often not in the name: "patio" finds bluestone, "driveway" finds the
   * aggregates. An empty query returns the whole catalog in order, so the same call drives both the
   * search box and the default listing.
   */
  search(query: string): StockTextureDefinition[] {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return this.list();

    const scored: { texture: StockTextureDefinition; score: number }[] = [];
    for (const texture of this.list()) {
      const name = texture.name.toLowerCase();
      let score = 0;
      if (name === needle) score = 100;
      else if (name.startsWith(needle)) score = 80;
      else if (name.includes(needle)) score = 60;
      else if (texture.tags.some((tag) => tag === needle)) score = 50;
      else if (texture.tags.some((tag) => tag.includes(needle))) score = 30;
      // Prefix in the *other* direction too, so a tag of `drive` is found by "driveway" and `cobble`
      // by "cobbles". Tagging every inflection of every word by hand is a losing game, and the
      // failure it prevents is the worst kind a picker has: a search that finds nothing and sends
      // someone off to forge a material that was already in the library.
      else if (texture.tags.some((tag) => tag.length >= 3 && needle.startsWith(tag))) score = 20;
      else if (texture.hint.toLowerCase().includes(needle)) score = 10;
      if (score > 0) scored.push({ texture, score });
    }
    // Stable within a score band: catalog order is authored, and shuffling equally good matches
    // between keystrokes makes a picker feel broken.
    return scored.sort((a, b) => b.score - a.score).map((entry) => entry.texture);
  }
}

/**
 * Validate a catalog record, throwing with a field-specific message.
 *
 * Throws rather than dropping, matching `assertValidFloraDefinition` and `assertValidPropDefinition`:
 * a catalog that silently ships eleven of the twelve materials it claims fails later, as a missing
 * entry in someone's picker, instead of here as a build error.
 */
export function assertValidStockTexture(raw: unknown): asserts raw is StockTextureDefinition {
  if (!raw || typeof raw !== 'object') throw new Error('Ground texture record is not an object.');
  const record = raw as Record<string, unknown>;
  const where = (field: string): string =>
    `Ground texture record ${typeof record.id === 'string' ? record.id : '<no id>'}: ${field}`;

  for (const field of ['id', 'name', 'file', 'hint', 'character'] as const) {
    const value = record[field];
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(where(`${field} must be a non-empty string.`));
    }
  }

  if (!STOCK_TEXTURE_FAMILIES.includes(record.family as StockTextureFamily)) {
    throw new Error(where(`family "${String(record.family)}" is not a known family.`));
  }

  if (
    !Array.isArray(record.tags) ||
    record.tags.some((tag) => typeof tag !== 'string' || tag.length === 0)
  ) {
    throw new Error(where('tags must be an array of non-empty strings.'));
  }

  if (typeof record.color !== 'string' || !/^#[0-9a-f]{6}$/i.test(record.color)) {
    throw new Error(where('color must be a #rrggbb string.'));
  }

  if (typeof record.plantable !== 'boolean') {
    throw new Error(where('plantable must be a boolean.'));
  }

  const roughness = record.roughness;
  if (typeof roughness !== 'number' || !Number.isFinite(roughness) || roughness < 0 || roughness > 1) {
    throw new Error(where('roughness must be a number between 0 and 1.'));
  }

  for (const field of ['tileMeters', 'grainMeters'] as const) {
    const value = record[field];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
      throw new Error(where(`${field} must be a positive number.`));
    }
  }

  // A tile has to hold several units of the material or it reads as a repeating pattern rather than
  // a surface — the same rule `parseTextureRecipe` applies to a forged material, enforced here at
  // build time where it can still be fixed by changing a number rather than by a user noticing.
  const tile = record.tileMeters as number;
  const grain = record.grainMeters as number;
  if (grain > tile) {
    throw new Error(where(`grainMeters (${grain}) cannot exceed tileMeters (${tile}).`));
  }
}

/** The default catalog loaded from the shipped `ground-textures.json`. */
export const stockTextures = new StockTextureCatalog(stockData);
